/**
 * Month view. On a phone each day shows colored dots (space is precious); from `sm` up it
 * shows the event titles. Color is never the only signal — the day button's aria-label
 * says how many events there are, and the agenda underneath spells every one of them out.
 */
import type { CalendarEvent, MemberSummary } from '../../shared/types';
import { WEEKDAYS, dayKey, formatDayLabel, memberOf, monthGridDays, tint, todayKey } from './lib';

interface Props {
  month: Date;
  eventsByDay: Map<string, CalendarEvent[]>;
  members: MemberSummary[];
  selectedDay: string | null;
  onSelectDay: (key: string) => void;
}

const MAX_CHIPS = 3;

export default function MonthGrid({
  month,
  eventsByDay,
  members,
  selectedDay,
  onSelectDay,
}: Props) {
  const days = monthGridDays(month);
  const today = todayKey();

  return (
    <div className="overflow-hidden rounded-card border border-border bg-surface">
      <div className="grid grid-cols-7 border-b border-border bg-surface-sunk">
        {WEEKDAYS.map((label) => (
          <div
            key={label}
            className="px-1 py-2 text-center text-[0.7rem] font-medium tracking-wide text-ink-faint uppercase"
          >
            <span aria-hidden="true">{label.slice(0, 1)}</span>
            <span className="sr-only">{label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = dayKey(day);
          const dayEvents = eventsByDay.get(key) ?? [];
          const inMonth = day.getMonth() === month.getMonth();
          const isToday = key === today;
          const isSelected = key === selectedDay;

          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectDay(key)}
              aria-pressed={isSelected}
              aria-label={`${formatDayLabel(key)}, ${
                dayEvents.length === 0
                  ? 'no events'
                  : `${dayEvents.length} event${dayEvents.length === 1 ? '' : 's'}`
              }`}
              className={[
                'relative flex min-h-[3.25rem] flex-col items-center gap-1 border-b border-r border-border p-1.5 text-left',
                'transition-colors focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-sage',
                'sm:min-h-[6rem] sm:items-stretch',
                inMonth ? 'bg-surface' : 'bg-surface-sunk/60',
                isSelected ? 'bg-sage-soft' : 'hover:bg-surface-sunk',
              ].join(' ')}
            >
              <span
                className={[
                  'grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs tabular-nums sm:self-start',
                  isToday ? 'bg-sage font-semibold text-white' : '',
                  !isToday && inMonth ? 'text-ink' : '',
                  !isToday && !inMonth ? 'text-ink-faint' : '',
                ].join(' ')}
              >
                {day.getDate()}
              </span>

              {/* phone: dots */}
              <span className="flex flex-wrap justify-center gap-0.5 sm:hidden" aria-hidden="true">
                {dayEvents.slice(0, 4).map((ev) => (
                  <span
                    key={ev.id}
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: memberOf(ev, members).color }}
                  />
                ))}
                {dayEvents.length > 4 && (
                  <span className="text-[0.6rem] leading-none text-ink-faint">+</span>
                )}
              </span>

              {/* tablet and up: the actual titles */}
              <span className="hidden w-full flex-col gap-0.5 sm:flex" aria-hidden="true">
                {dayEvents.slice(0, MAX_CHIPS).map((ev) => {
                  const owner = memberOf(ev, members);
                  return (
                    <span
                      key={ev.id}
                      className="flex items-center gap-1 truncate rounded px-1 py-0.5 text-[0.7rem] leading-tight text-ink"
                      style={{ backgroundColor: tint(owner.color) }}
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: owner.color }}
                      />
                      <span className="truncate">{ev.title}</span>
                    </span>
                  );
                })}
                {dayEvents.length > MAX_CHIPS && (
                  <span className="px-1 text-[0.68rem] text-ink-faint">
                    +{dayEvents.length - MAX_CHIPS} more
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
